import {
  cloneDeep,
  isEmpty,
  isNil,
  isArray,
  sortBy,
  isPlainObject,
  isEqual,
  capitalize,
} from "lodash"
import i18n from "@/i18n"

import { MetricMapType, ReportActivitySessionProps } from "@/app/report/common/type"
import { SummaryImpl } from "@/app/report/common/constructor"
import { getDurationString, getSecondsToMMSS } from "@/utils/format"
import { Unit } from "@/types/setting"
import { MetricMeta } from "@/types/metric"
import { SummaryActionMetricType } from "@/types/report"
import { convertUnitSquare } from "@/utils/helper"

const localeKeys: { [key: string]: string } = {
  en: "en-US",
  es: "es-ES",
  ja: "ja-JP",
  "zh-Hant": "zh-CN",
  pt: "pt-PT",
  ko: "ko-KR",
}

// 레거시 템플릿의 실수 저장값(0.1, 0.01)을 소수 자릿수(1, 2)로 보정
/**
 * 소수 자릿수 선택과 무관하게 항상 정수로 표시하는 단위 (#169 / ERR-013).
 * 심박(bpm)은 정본 표기 관례가 정수다 — 서버는 1자리 소수로 내려주므로 프론트 표시 단계에서 일괄 반올림한다.
 */
const INTEGER_UNITS = new Set(["bpm", "heartrate"])

export function isIntegerUnit(unit?: string | null): boolean {
  if (!unit) {
    return false
  }

  // 표시 단위("bpm")와 메트릭 메타의 단위 유형 자리표시자("{heartRate}") 둘 다 인식
  return INTEGER_UNITS.has(unit.replace(/[{}]/g, "").trim().toLowerCase())
}

/**
 * 단위 규칙을 반영한 최종 소수 자릿수. 사용자가 고른 자릿수(리포트 상단 셀렉터·섹션 displayOptions)보다
 * 단위 규칙(정수 단위)이 우선한다. 값을 문자열로 만드는 모든 경로가 이 함수를 거쳐야 한다.
 */
export function resolveDecimalPoint(unit: string | null | undefined, decimalPoint: number): number {
  return isIntegerUnit(unit) ? 0 : decimalPoint
}

export function normalizeDecimalPoint(decimalPoint: number): number {
  if (Number.isInteger(decimalPoint)) {
    return decimalPoint
  }

  return Math.max(0, Math.round(-Math.log10(decimalPoint)))
}

export function getLocaleString(value: number, decimalPoint: number): string {
  const currentLocale = i18n.language

  return value.toLocaleString(localeKeys[currentLocale], {
    minimumFractionDigits: 0,
    maximumFractionDigits: normalizeDecimalPoint(decimalPoint),
  })
}

export function getDateTimeString(): string {
  const now = new Date()
  const year = String(now.getFullYear()).padStart(4, "0")
  const month = String(now.getMonth() + 1).padStart(2, "0")
  const day = String(now.getDate().toString().padStart(2, "0"))
  const hours = String(now.getHours()).padStart(2, "0")
  const minutes = String(now.getMinutes()).padStart(2, "0")
  const seconds = String(now.getSeconds()).padStart(2, "0")

  return `${year}${month}${day}_${hours}${minutes}${seconds}`
}

export function getMetricValueStr({
  value,
  unit,
  calcType,
  decimalPoint,
  metric,
  showUnit = true,
}: {
  value: number | string
  unit?: string | null
  decimalPoint?: number | null
  calcType?: string
  metric?: string
  showUnit?: boolean
}): string {
  if (isNil(value)) {
    return "-"
  }

  const lowerCasedUint = unit?.toLowerCase()
  if (typeof value === "string") {
    return value.toString()
  }

  if (lowerCasedUint?.includes("action_occurrence")) {
    return getSecondsToMMSS(value)
  }

  if (
    lowerCasedUint === "sec" ||
    lowerCasedUint === "min sec" ||
    lowerCasedUint?.includes("action_min sec")
  ) {
    return getDurationString(value / 60)
  }

  if ((lowerCasedUint === "times" || metric === "rpe") && calcType === "total") {
    return Math.floor(value).toLocaleString()
  }

  // 정수 단위(bpm)는 호출자가 넘긴 자릿수를 무시하고 0 으로 (#169)
  const normalizedDecimalPoint = resolveDecimalPoint(unit, normalizeDecimalPoint(decimalPoint || 0))
  let returnVal = value?.toLocaleString(undefined, {
    minimumFractionDigits: normalizedDecimalPoint,
    maximumFractionDigits: normalizedDecimalPoint,
  })

  if (showUnit && unit && unit !== "times") {
    returnVal = returnVal.concat(
      " ",
      (showUnit && unit && unit !== "times" && convertUnitSquare(unit)) || "",
    )
  }

  return returnVal
}

export function removeSpecialCharacter(value: string): string {
  if (isEmpty(value)) {
    return ""
  } else {
    return value.replace(/[^\p{L}\p{P}\p{Z}\d\s`~!@#$%^&*()-_=+[\]{}|;:\\'"<>,./?]+/gu, "")
  }
}

export const parseUnit = (
  unit?: Unit | MetricMeta | string,
  selectedUnitList?: Unit[],
  metricMap?: MetricMapType,
): string | null => {
  if (!unit) {
    return null
  }

  if (TypeChecker.isString(unit)) {
    const result = convertString(unit, metricMap, selectedUnitList)
    if (result === "sec") {
      return "min sec"
    }

    return result || null
  }

  if (Object.hasOwnProperty.call(unit, "path")) {
    const result = convertMetricMeta(unit as MetricMeta, selectedUnitList)

    if (result === "sec") {
      return "min sec"
    }

    return result || null
  }

  if (Object.hasOwnProperty.call(unit, "idfUnit") && selectedUnitList) {
    const result = selectedUnitList.find(
      (selectedUnit) => (unit as Unit).idfUnit === selectedUnit.idfUnit,
    )?.unit

    if (result === "sec") {
      return "min sec"
    }

    return result || null
  }

  return null
}

export class TypeChecker {
  static isString(value: unknown): value is string {
    return typeof value === "string"
  }

  static isNumber(value: unknown): value is number {
    return typeof value === "number"
  }

  static isDate(value: unknown): value is Date {
    return value instanceof Date
  }

  static isArray(value: unknown): value is unknown[] {
    return Array.isArray(value)
  }

  static isObject(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
  }
}

export const convertMetricMeta = (metric?: MetricMeta, selectedUnitList?: Unit[]) => {
  if (!metric || !selectedUnitList) {
    return
  }
  const { unit } = metric
  const regex = new RegExp(/({|})/, "g")

  const metricType = unit?.replace(regex, "")

  const selectedMetric = selectedUnitList.find((selectedUnit) => selectedUnit.type === metricType)

  return selectedMetric?.unit || metricType
}

export const convertString = (
  value?: string,
  metricMap?: MetricMapType,
  selectedUnitList?: Unit[],
) => {
  if (!value) {
    return
  }

  if (value === "action_distance") {
    return selectedUnitList?.find((selectedUnit) => selectedUnit.type === value)?.unit
  }

  if (!metricMap) {
    return
  }
  const metric = metricMap.get(value)

  return convertMetricMeta(metric, selectedUnitList)
}

export function getDecimalPoint(metricKey: string | undefined = "", unit: string | null): number {
  let decimalPoint = 1

  switch (unit) {
    case "times":
    case "count":
      decimalPoint = 0
      break
    case null:
      decimalPoint = metricKey === "rpe" ? 0 : 1
      break
  }

  return resolveDecimalPoint(unit, decimalPoint)
}

export const removeSpecialCharacterInAllSource = ({
  activity,
  session,
}: ReportActivitySessionProps) => {
  if (session) {
    const newSession = cloneDeep(session)

    newSession.intervalSummary?.forEach((interval) => {
      interval?.forEach((summary) => {
        if (summary.sessionName) {
          summary.sessionName = removeSpecialCharacter(summary.sessionName)
        }

        summary.summaries?.forEach((summaryInner) => {
          summaryInner?.playerSummary?.players?.forEach((player) => {
            if (player && player.playerName) {
              player.playerName = removeSpecialCharacter(player.playerName)
            }
          })
        })
      })
    })

    newSession.playerSessionSummary?.players?.forEach((player) => {
      if (player && player.playerName) {
        player.playerName = removeSpecialCharacter(player.playerName)
      }
    })

    newSession.playerSummary?.forEach((player) => {
      if (player && player.sessionName) {
        player.sessionName = removeSpecialCharacter(player.sessionName)
      }
    })
  }

  if (activity) {
    const newActivity = cloneDeep(activity)
    newActivity.intervalSummary?.forEach((intervalSummary) => {
      intervalSummary?.forEach((summary) => {
        summary?.summaries?.forEach((summ) => {
          summ.playerSummary?.players?.forEach((player) => {
            if (player && player.playerName) {
              player.playerName = removeSpecialCharacter(player.playerName)
            }
          })
        })
      })
    })

    newActivity.playerActivitySummary?.players?.forEach((player) => {
      if (player && player.playerName) {
        player.playerName = removeSpecialCharacter(player.playerName)
      }
    })
  }
}

export function getValue(obj: any, path: string[]) {
  try {
    return path.reduce((o, i) => o[i], obj)
  } catch {
    return null
  }
}

export function getDecimalPointForTick(
  decimalPoint: number,
  valueMax: number,
  ticksMax: number,
): number {
  if (decimalPoint === 0 || valueMax > ticksMax) {
    return 0
  }
  if (valueMax > ticksMax / 10) {
    return 1
  }

  return 2
}

export function getTicks(decimalPoint: number, valueMax: number, ticksMax: number): number {
  if (decimalPoint === 0 && valueMax < ticksMax) {
    return valueMax
  }

  return ticksMax
}

export const isEqualUnordered = (a: any, b: any): boolean => {
  if (isArray(a) && isArray(b)) {
    if (a.length !== b.length) {
      return false
    }
    const sortedA = sortBy(a, JSON.stringify)
    const sortedB = sortBy(b, JSON.stringify)

    return isEqual(sortedA, sortedB)
  } else if (isPlainObject(a) && isPlainObject(b)) {
    const aKeys = Object.keys(a)
    const bKeys = Object.keys(b)
    if (!isEqual(aKeys.sort(), bKeys.sort())) {
      return false
    }

    return aKeys.every((key) => isEqualUnordered(a[key], b[key]))
  } else {
    return isEqual(a, b)
  }
}

export const parseSummary = (original: SummaryImpl | Record<string, any>): SummaryImpl => {
  const targetSummaryRaw = original

  if (targetSummaryRaw instanceof SummaryImpl) {
    return targetSummaryRaw
  }

  const summary = new SummaryImpl()

  for (const type in targetSummaryRaw) {
    const typeData = targetSummaryRaw[type as keyof typeof targetSummaryRaw]
    if (!typeData) {
      continue
    }

    for (const key of Object.keys(typeData)) {
      const value = typeData[key]
      if (!Array.isArray(value)) {
        summary.set(key, value, type)
        continue
      }

      if (key.match(/^(.*)Zone([A-Za-z]+)$/)) {
        const match = key.match(/^(.*)Zone([A-Za-z]+)$/)

        if (match) {
          const zoneType = match[1]
          const suffix = match[2]

          ;(value as number[]).forEach((v: number, idx: number) => {
            const singularSuffix = suffix === "Entries" ? "Entries" : suffix.slice(0, -1)
            const startIndex = suffix === "Entries" ? 2 : 1
            const zoneKey = `${zoneType}Zone${idx + startIndex}${singularSuffix}`
            summary.set(zoneKey, v, type)
          })
        }
      }
      if (key.includes("Effort")) {
        ;(value as SummaryActionMetricType[]).forEach((v: SummaryActionMetricType, idx: number) => {
          for (const effortValue in v) {
            if (effortValue === "actionCode") {
              continue
            }
            const splitEffortKey = key.split("Effort")[0]
            if (effortValue.includes("max")) {
              const trimmedEffortValue = effortValue.split("max")[1]
              const effortKey = `max${capitalize(splitEffortKey)}Effort${idx + 1}${trimmedEffortValue}`
              summary.set(
                effortKey,
                v[effortValue as keyof SummaryActionMetricType] as number,
                type,
              )
            } else {
              const effortKey = `${splitEffortKey}Effort${idx + 1}${capitalize(effortValue)}`
              summary.set(
                effortKey,
                v[effortValue as keyof SummaryActionMetricType] as number,
                type,
              )
            }
          }
        })
      }
    }
  }

  return summary
}

/**
 * SummaryImpl에서 메트릭 값을 추출하는 공통 함수
 * - Custom, Rel suffix는 SummaryImpl.get()에서 자동 처리됨
 */
export const getSummaryImplValue = (
  metricKey: string,
  source: unknown,
  metricMap: MetricMapType,
  selectedUnitList?: Unit[],
): { value: number | null; unit: string | null } => {
  if (!(source instanceof SummaryImpl)) {
    return { value: null, unit: null }
  }

  const metric = metricMap.get(metricKey)
  const unit = parseUnit(metric, selectedUnitList, metricMap)
  const path = metric?.path[0] || "general"

  const rawValue = source.get(metricKey, path)

  if (rawValue === null || rawValue === undefined) {
    return { value: null, unit }
  }

  if (typeof rawValue === "object" && "value" in rawValue) {
    const innerValue = rawValue.value
    if (innerValue === null || innerValue === undefined) {
      return { value: null, unit }
    }
    return { value: Number(innerValue), unit }
  }

  return { value: Number(rawValue), unit }
}

/**
 * 표 컬럼 최소 폭 계산용 텍스트 폭.
 *
 * 기본값(패딩 32 + 아이콘 20)은 화면용이다. PDF 출력에는 정렬 아이콘이 표시되지 않으므로
 * 아이콘 폭을 빼고 패딩도 줄이면, 글자 크기를 그대로 두고도 표 전체 폭이 크게 줄어든다
 * (액션 표 실측: 9컬럼 1000px → 630px, 순수 글자 폭 합은 504px).
 */
export const getTextWidth = (text: string, paddingWidth = 32, iconWidth = 20) => {
  const font = "14px 'pretendard', 'Noto Sans SC', 'Noto Sans KR', sans-serif"
  const canvas = document.createElement("canvas")
  const context = canvas.getContext("2d")
  if (!context) {
    return 100
  }
  context.font = font

  return context.measureText(text).width + paddingWidth + iconWidth
}

export const pxToMm = (px: number) => {
  return px * 0.265
}
